import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Tab } from '@mui/material';
import { BookingWizardFlowTypes } from 'common/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import React, { FC, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  GetSimulatorLocationsAdapter,
  IGetSimulatorLocationsHook,
} from 'application/modules/bookingWizard/useCases/hooks/useCaseGetSimulatorLocations';
import useCaseSelectClubFitting from 'application/modules/bookingWizard/useCases/hooks/useCaseSelectClubFitting';
import { WizardStep } from 'domain/entities/WizardStep';
import { useHookInjection } from 'domain/hooks';
import NavigationBar from 'infrastructure/components/NavigationBar';
import PageLayout from 'infrastructure/components/PageLayout';
import {
  selectExperienceType,
  selectLocation,
  selectLocationDetails,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import { setExperienceType, setLocation, setLocationDetails } from 'infrastructure/redux/slices/bookingWizard.slice';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
import LocationSelect from 'infrastructure/targets/web/modules/bookingWizard/components/LocationSelect';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';

import ClubFittingByCoach from './components/FittingByCoach';
import ClubFittingbyTime from './components/FittingByTime';
import { StyledContentContainer } from './style';

interface ISelectFittingTypesStep {
  stepName: WizardStep;
}
const SelectFittingTypesStep: FC<ISelectFittingTypesStep> = ({ stepName }) => {
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const t = useTranslationPrefix('book.select_fitting_type');
  const selectedLocationId = useAppSelector(selectLocation);
  const { clubFittingTypes, prevStep, nextStep, SelectClubFittingType } =
    useCaseSelectClubFitting(stepName);
  const selectedLocationDetails = useAppSelector(selectLocationDetails);
  const phoneNumber = selectedLocationDetails?.telephone;
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const locationSlug = query?.get('location');
  const selectedExperienceType = useAppSelector(selectExperienceType);
  const [activeTab, setActiveTab] = React.useState(
    selectedExperienceType ?? BookingWizardFlowTypes.ClubFitting,
  );

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    dispatch(setExperienceType({ experienceType: newValue }));
  };

  const getLocationsHook = useHookInjection<IGetSimulatorLocationsHook>(
    GetSimulatorLocationsAdapter,
  );
  const { result: locations, inProgress: areLocationsFetching } = getLocationsHook;

  useEffect(() => {
    if (!areLocationsFetching && locations && locationSlug) {
      const locationToPreselect = locations.find((location) => location.urlSlug === locationSlug);
      if (locationToPreselect) {
        dispatch(setLocation(locationToPreselect.id));
        dispatch(setLocationDetails(locationToPreselect));
      }
    }
  }, [locations, locationSlug, areLocationsFetching, dispatch]);

  useEffect(() => {
    if (selectedLocationId) {
      SelectClubFittingType(selectedLocationId);
    }
  }, [selectedLocationId, SelectClubFittingType]);

  return (
    <PageLayout pageTitle={t('heading')} shouldFadeIn>
      <NavigationBar backButtonCallback={prevStep}>
        <LocationSelect selectedSimLocationId={selectedLocationId} />
      </NavigationBar>
      <StyledContentContainer>
        <TabContext value={activeTab}>
          <Box sx={{ paddingBottom: 6 }}>
            <TabList onChange={handleChange} indicatorColor="primary" centered variant="fullWidth">
              <Tab
                label={t('search_by_time')}
                value={BookingWizardFlowTypes.ClubFitting}
                sx={{
                  textTransform: 'none',
                  fontWeight: activeTab === BookingWizardFlowTypes.ClubFitting ? 600 : 400,
                  fontSize: 16,
                }}
              />
              <Tab
                label={t('search_by_coach')}
                value={BookingWizardFlowTypes.ClubFittingbyCoach}
                sx={{
                  textTransform: 'none',
                  fontWeight: activeTab === BookingWizardFlowTypes.ClubFittingbyCoach ? 600 : 400,
                  fontSize: 16,
                }}
              />
            </TabList>
          </Box>
          <TabPanel value={BookingWizardFlowTypes.ClubFitting}>
            <ClubFittingbyTime
              heading={t('fitting_type_heading')}
              ctaContent={t('reserve_now')}
              clubFittingTypes={clubFittingTypes}
              phoneNumber={phoneNumber}
              nextStep={nextStep}
            />
          </TabPanel>
          <TabPanel value={BookingWizardFlowTypes.ClubFittingbyCoach}>
            <ClubFittingByCoach
              heading={t('coaches_heading')}
              phoneNumber={phoneNumber}
              stepName={WizardStep.selectFittingType}
            />
          </TabPanel>
        </TabContext>
      </StyledContentContainer>
    </PageLayout>
  );
};

export default SelectFittingTypesStep;
